import { useMemo } from 'react';
import { Icon } from './Icon';

/**
 * Minimal JSON Schema → form generator. Supports the slice of draft
 * 2020-12 the platform's attribute dictionary actually uses today:
 *
 *   • type: object with `properties`, optional `required` array, and
 *     `additionalProperties` ignored on the UI side (the backend stays
 *     tolerant per the strategy doc anyway).
 *   • Property types: string (with optional `enum`, `maxLength`,
 *     `pattern`), integer (with `minimum` / `maximum`), boolean, nested
 *     object (rendered as a fieldset).
 *
 * The generator deliberately does NOT try to be a full JSON Schema form
 * library. The aim is "good enough to declare the 4–5 attribute types
 * the back-office operators will use manually" without dragging in a
 * 300 KB dep tree.
 */

export type JSONSchema = {
  type?: 'object' | 'string' | 'integer' | 'number' | 'boolean' | 'array';
  properties?: Record<string, JSONSchema>;
  required?: string[];
  enum?: Array<string | number>;
  format?: string;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  pattern?: string;
  description?: string;
  additionalProperties?: boolean;
  items?: JSONSchema;
};

export type SchemaFormErrors = Record<string, string>;

type SchemaFormProps = {
  schema: JSONSchema;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  errors?: SchemaFormErrors;
  path?: string; // dotted path for nested objects ("gps.latitude")
};

export function SchemaForm({ schema, value, onChange, errors, path = '' }: SchemaFormProps) {
  const properties = schema.properties ?? {};
  const required = useMemo(() => new Set(schema.required ?? []), [schema.required]);
  const fields = Object.entries(properties);

  if (fields.length === 0) {
    return (
      <div className="muted" style={{ fontSize: 13, fontWeight: 500, padding: 10 }}>
        Aucun champ déclaré dans le schéma.
      </div>
    );
  }

  function setField(name: string, v: unknown) {
    const next = { ...value };
    if (v === '' || v == null) {
      delete next[name];
    } else {
      next[name] = v;
    }
    onChange(next);
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 16,
      }}
    >
      {fields.map(([name, propSchema]) => {
        const fullPath = path ? `${path}.${name}` : name;
        const fieldError = errors?.[fullPath];
        const isRequired = required.has(name);
        const isWide = needsFullWidth(propSchema);
        return (
          <div
            key={name}
            className="field"
            style={{ gridColumn: isWide ? '1 / -1' : undefined }}
          >
            <label>
              {name}
              {isRequired && <span style={{ color: 'var(--alert-ink)' }}> requis</span>}
              <span className="muted" style={{ fontWeight: 600 }}> · {propSchema.type ?? 'string'}</span>
            </label>
            <FieldRenderer
              schema={propSchema}
              value={value[name]}
              onChange={(v) => setField(name, v)}
              path={fullPath}
              parentErrors={errors}
            />
            {propSchema.description && <div className="hint">{propSchema.description}</div>}
            {fieldError && (
              <div className="hint" style={{ color: 'var(--alert-ink)' }}>{fieldError}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

type FieldRendererProps = {
  schema: JSONSchema;
  value: unknown;
  onChange: (v: unknown) => void;
  path: string;
  parentErrors?: SchemaFormErrors;
};

function FieldRenderer({ schema, value, onChange, path, parentErrors }: FieldRendererProps) {
  const t = schema.type ?? 'string';

  if (schema.enum && schema.enum.length > 0) {
    return (
      <select
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">Choisir…</option>
        {schema.enum.map((opt) => (
          <option key={String(opt)} value={String(opt)}>
            {String(opt)}
          </option>
        ))}
      </select>
    );
  }

  if (t === 'boolean') {
    const isOn = value === true;
    const isOff = value === false;
    return (
      <div className="seg">
        <button type="button" className={isOn ? 'on' : ''} onClick={() => onChange(true)}>Oui</button>
        <button type="button" className={isOff ? 'on' : ''} onClick={() => onChange(false)}>Non</button>
      </div>
    );
  }

  if (t === 'integer' || t === 'number') {
    return (
      <input
        type="number"
        value={value == null ? '' : String(value)}
        min={schema.minimum}
        max={schema.maximum}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') onChange(undefined);
          else onChange(t === 'integer' ? Number.parseInt(v, 10) : Number.parseFloat(v));
        }}
        placeholder={t === 'integer' ? '0' : '0.0'}
      />
    );
  }

  if (t === 'object') {
    const nestedValue =
      typeof value === 'object' && value != null ? (value as Record<string, unknown>) : {};
    return (
      <div
        style={{
          padding: '12px 14px',
          border: '1px solid var(--line)',
          borderRadius: 10,
          background: 'var(--panel-2)',
        }}
      >
        <SchemaForm
          schema={schema}
          value={nestedValue}
          onChange={(v) => onChange(Object.keys(v).length === 0 ? undefined : v)}
          errors={parentErrors}
          path={path}
        />
      </div>
    );
  }

  if (t === 'string') {
    const long = (schema.maxLength ?? 0) >= 200;
    if (long) {
      return (
        <textarea
          value={(value as string) ?? ''}
          maxLength={schema.maxLength}
          onChange={(e) => onChange(e.target.value || undefined)}
          placeholder="Saisir"
        />
      );
    }
    return (
      <input
        type="text"
        value={(value as string) ?? ''}
        maxLength={schema.maxLength}
        onChange={(e) => onChange(e.target.value || undefined)}
        placeholder="Saisir"
      />
    );
  }

  // Fallback: render as plain text input. Anything else (arrays for
  // instance) lands here; the back-office operators won't see those in
  // the manual-declaration form for slice 3.
  return (
    <input
      type="text"
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value || undefined)}
      placeholder="Saisir"
    />
  );
}

function needsFullWidth(s: JSONSchema): boolean {
  if (s.type === 'object') return true;
  if (s.type === 'string' && (s.maxLength ?? 0) >= 200) return true;
  return false;
}

/**
 * Validates a value against the schema's `required` list and returns a
 * dotted-path-keyed map of human-readable messages. We deliberately
 * don't run full JSON Schema validation here — the backend keeps its
 * "tolerant payload" policy, so checking required is enough to catch
 * obvious submission mistakes.
 */
export function validateRequired(schema: JSONSchema, value: Record<string, unknown>, basePath = ''): SchemaFormErrors {
  const errors: SchemaFormErrors = {};
  if (schema.type !== 'object') return errors;
  const required = schema.required ?? [];
  for (const name of required) {
    const v = value?.[name];
    const path = basePath ? `${basePath}.${name}` : name;
    if (v === undefined || v === null || v === '' ||
        (typeof v === 'object' && v != null && !Array.isArray(v) && Object.keys(v as object).length === 0)) {
      errors[path] = 'Champ requis';
    }
  }
  // Recurse into nested objects so e.g. `gps.latitude` is checked when
  // `gps` is itself flagged required.
  for (const [name, sub] of Object.entries(schema.properties ?? {})) {
    if (sub.type === 'object' && value?.[name] != null) {
      const nested = value[name] as Record<string, unknown>;
      const nestedPath = basePath ? `${basePath}.${name}` : name;
      Object.assign(errors, validateRequired(sub, nested, nestedPath));
    }
  }
  return errors;
}

/**
 * Convenience: an icon explaining "we generated this from a JSON
 * Schema" — shown next to the form title in modals.
 */
export function SchemaBadge({ version }: { version: number }) {
  return (
    <span className="meta acc" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Icon name="cmd" size={11} />
      schéma v{version}
    </span>
  );
}
